/**
 * useError Hook
 * Custom hook for error management
 */

import { useCallback, useContext } from 'react';

import { ERROR_CODES, ErrorContext, ErrorSeverity } from './errorContextTypes';

export function useError() {
  const context = useContext(ErrorContext);

  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }

  const { addError, removeError, clearErrors, getErrorCount, showError, errors } = context;

  // Helper to show info message
  const showInfo = useCallback(
    (message: string, details?: any) => {
      addError({ message, severity: 'info', details });
    },
    [addError]
  );

  // Helper to show warning
  const showWarning = useCallback(
    (message: string, details?: any) => {
      addError({ message, severity: 'warning', details });
    },
    [addError]
  );

  // Helper to show critical error
  const showCritical = useCallback(
    (message: string, details?: any) => {
      addError({ message, severity: 'critical', details });
    },
    [addError]
  );

  // Helper to handle async errors
  const handleAsyncError = useCallback(
    (error: any, customMessage?: string) => {
      const message = customMessage || error.message || 'An unexpected error occurred';
      const code = error.code || ERROR_CODES.UNKNOWN_ERROR;

      addError({
        message,
        severity: 'error',
        code,
        details: error,
      });
    },
    [addError]
  );

  // Helper to show error with action
  const showErrorWithAction = useCallback(
    (
      message: string,
      actionLabel: string,
      actionHandler: () => void,
      severity: ErrorSeverity = 'error'
    ) => {
      addError({
        message,
        severity,
        action: {
          label: actionLabel,
          handler: actionHandler,
        },
      });
    },
    [addError]
  );

  // Helper for network errors
  const showNetworkError = useCallback(
    (details?: any) => {
      addError({
        message: 'Network connection error',
        severity: 'error',
        code: ERROR_CODES.NETWORK_ERROR,
        details,
      });
    },
    [addError]
  );

  // Helper for validation errors
  const showValidationError = useCallback(
    (message: string, field?: string) => {
      addError({
        message,
        severity: 'warning',
        code: ERROR_CODES.VALIDATION_FAILED,
        details: { field },
      });
    },
    [addError]
  );

  // Helper for storage errors
  const showStorageError = useCallback(
    (type: 'full' | 'corrupt' = 'corrupt') => {
      const code = type === 'full' ? ERROR_CODES.STORAGE_FULL : ERROR_CODES.STORAGE_CORRUPT;
      addError({
        message:
          type === 'full' ? 'Browser storage is full' : 'Storage data appears to be corrupted',
        severity: 'critical',
        code,
      });
    },
    [addError]
  );

  // Check if there are errors of a specific severity
  const hasErrors = useCallback(
    (severity?: ErrorSeverity) => {
      return getErrorCount(severity) > 0;
    },
    [getErrorCount]
  );

  // Get the latest error
  const getLatestError = useCallback(() => {
    return errors[errors.length - 1] || null;
  }, [errors]);

  return {
    // Core functions
    addError,
    removeError,
    clearErrors,
    showError, // Backward compatibility

    // Helper functions
    showInfo,
    showWarning,
    showCritical,
    handleAsyncError,
    showErrorWithAction,
    showNetworkError,
    showValidationError,
    showStorageError,

    // State queries
    errors,
    hasErrors,
    getErrorCount,
    getLatestError,

    // Error codes for consistency
    ERROR_CODES,
  };
}
